import React, { useEffect, useState } from "react";
import {
  AppBar, Toolbar, Box, IconButton, useTheme,
  Breadcrumbs, Chip, Fade, Typography, Avatar, Menu, MenuItem, Divider,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import HomeIcon from "@mui/icons-material/Home";
import LogoutIcon from "@mui/icons-material/Logout";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";

import logo from "../assets/logoNouha.png";
import AnimatedLogo from "./Elements/AnimatedLogo";
import { useClientAuth } from "../contexts/ClientAuthContext";

const iconSx = { fontSize: 16 };

export default function ClientLayout() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { client, logout } = useClientAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const [blReference, setBlReference] = useState(null);

  useEffect(() => { /* on every nav change, the menu auto-closes */ setAnchorEl(null); }, [location.pathname]);

  useEffect(() => {
    if (!location.pathname.startsWith("/bl/")) setBlReference(null);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const initials = (client?.company_name || client?.email || "?")
    .split(/\s+/).filter(Boolean).slice(0, 2)
    .map((s) => s[0]?.toUpperCase()).join("");

  const getBreadcrumbs = () => {
    const path = location.pathname;
    const home = { label: "Mes BL", path: "/", icon: <HomeIcon sx={iconSx} /> };
    if (path === "/") return [{ ...home, current: true }];
    if (path.startsWith("/bl/")) {
      const label = blReference || (id ? `BL #${id}` : "Détail");
      return [
        home,
        { label, path, icon: <LocalShippingIcon sx={iconSx} />, current: true },
      ];
    }
    return [home];
  };

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          background: "linear-gradient(120deg, #f7fff4 0%, #d0f0bb 100%)",
          borderBottom: "3px solid #6db43d",
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ position: "relative" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Fade in timeout={1200}>
              <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center" }}>
                <Breadcrumbs
                  separator={<NavigateNextIcon fontSize="small" sx={{ color: "#6db43d" }} />}
                  sx={{
                    flexWrap: "wrap",
                    rowGap: 0.5,
                    "& .MuiBreadcrumbs-separator": { color: "#6db43d" },
                  }}
                >
                  {getBreadcrumbs().map((crumb, i) => (
                    <Chip
                      key={i}
                      label={crumb.label}
                      icon={crumb.icon}
                      onClick={() => !crumb.current && navigate(crumb.path)}
                      sx={{
                        backgroundColor: crumb.current ? "rgba(109, 180, 61, 0.15)" : "rgba(109, 180, 61, 0.08)",
                        color: crumb.current ? "#6db43d" : "#666",
                        fontWeight: crumb.current ? 600 : 500,
                        cursor: crumb.current ? "default" : "pointer",
                        transition: "all 0.3s ease",
                        "&:hover": !crumb.current ? {
                          backgroundColor: "rgba(109, 180, 61, 0.2)",
                          transform: "translateY(-1px)",
                        } : {},
                        "& .MuiChip-icon": { color: crumb.current ? "#6db43d" : "#666" },
                      }}
                    />
                  ))}
                </Breadcrumbs>
              </Box>
            </Fade>
          </Box>

          {/* Logo Nouha centre */}
          <Box sx={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            display: { xs: "none", sm: "block" },
          }}>
            <AnimatedLogo src={logo} height={40} variant="default" onClick={() => navigate("/")} showAnimation />
          </Box>
          <Box sx={{
            flexGrow: 1,
            display: { xs: "flex", sm: "none" },
            justifyContent: "center",
          }}>
            <AnimatedLogo src={logo} height={32} onClick={() => navigate("/")} showAnimation />
          </Box>

          {client && (
            <Box sx={{
              position: { xs: "static", sm: "absolute" },
              right: { sm: 20 },
              display: "flex",
              alignItems: "center",
              gap: 1,
              ml: { xs: "auto", sm: 0 },
            }}>
              <Box sx={{ display: { xs: "none", sm: "block" }, textAlign: "right", mr: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#4f8c25", lineHeight: 1.1 }}>
                  {client.company_name}
                </Typography>
                <Typography variant="caption" sx={{ color: "#666" }}>
                  {client.email}
                </Typography>
              </Box>
              <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} size="small">
                <Avatar sx={{ bgcolor: "#6db43d", width: 36, height: 36, fontSize: 14, fontWeight: 600 }}>
                  {initials}
                </Avatar>
              </IconButton>
              <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
                <MenuItem disabled>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{client.company_name}</Typography>
                    <Typography variant="caption" color="text.secondary">{client.email}</Typography>
                  </Box>
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout}>
                  <LogoutIcon fontSize="small" sx={{ mr: 1, color: "#f27c1e" }} />
                  Se déconnecter
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Box component="main" sx={{ flex: 1, mt: 8, p: { xs: 2, sm: 3 }, bgcolor: "#f9fafc" }}>
        <Outlet context={{ setBlReference }} />
      </Box>
    </Box>
  );
}
